import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import api from '../api';

const PROJECT_LOGOS = {
  'ד החדשה': '/logos/jaffad.png',
  'אייזיקחריף': '/logos/aizik_harif.png',
  'ג החדשה': '/logos/jaffag.png',
  'סקייליין בת ים': '/logos/skyline.png',
  'ראשון החדשה': '/logos/rishon.jpg',
  'אפלטון 6': '/logos/aplaton6.jpeg',
  'שארית ישראל': '/logos/sherit_israel.jpg',
};

// "ג החדשה" is the literal project_name in the DB (used for API calls and
// matching) — this only changes what's shown to the user.
const PROJECT_DISPLAY_NAMES = { 'ג החדשה': "ג' החדשה", 'אייזיקחריף': 'אייזיק חריף' };
const displayProjectName = n => PROJECT_DISPLAY_NAMES[n] || n;

// לוגו מותאם לפרויקטים של גבסו (יוסי אבוטבול)
const GAVSO_LOGOS = {
  'ג החדשה': '/logos/jaffag-clean.jpg',
  'אייזיקחריף': '/logos/aizik_harif.png',
};

const GAVSO_PROJECTS = ['ג החדשה', 'אייזיקחריף'];

const GAVSO_LOGO_HEIGHT = {
  'אייזיקחריף': 110,
  'ג החדשה': 110,
};

const CLIENT_LOGOS = {
  'סקייליין בת ים': '/logos/aura_logo.jpg',
  'ד החדשה': '/logos/aura_logo.jpg',
};

function getWorkerRole() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'project_manager';
    const p = JSON.parse(decodeURIComponent(escape(atob(token.split('.')[1]))));
    return p.role || 'project_manager';
  } catch { return 'project_manager'; }
}

function WelcomePage({ project }) {
  const navigate = useNavigate();
  const name = project.projectName;
  const clientLogo = CLIENT_LOGOS[name];
  const projectLogo = PROJECT_LOGOS[name];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f2d55 0%, #1a4a8a 60%, #1e5fa8 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      position: 'relative', direction: 'rtl',
    }}>
      {/* לוגו Community בפינה */}
      <div style={{ position: 'absolute', top: 20, right: 24 }}>
        <img src="/logo.jpg" alt="Community" style={{ height: 36, objectFit: 'contain', borderRadius: 6, opacity: 0.85 }}
          onError={e => e.target.style.display = 'none'} />
      </div>

      {/* כרטיס מרכזי */}
      <div
        onClick={() => navigate(`/project-hub/${encodeURIComponent(name)}`)}
        style={{
          background: 'white', borderRadius: 24, padding: '60px 80px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
          cursor: 'pointer', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          animation: 'fadeIn 0.5s ease',
          transition: 'transform 0.2s, box-shadow 0.2s',
          maxWidth: 420, width: '90%',
        }}
        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 28px 70px rgba(0,0,0,0.4)'; }}
        onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.3)'; }}
      >
        {clientLogo ? (
          <img src={clientLogo} alt={name} style={{ maxHeight: 160, maxWidth: 280, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
        ) : projectLogo ? (
          <img src={projectLogo} alt={name} style={{ maxHeight: 140, maxWidth: 260, objectFit: 'contain' }} />
        ) : (
          <div style={{ fontSize: '5rem' }}>🏢</div>
        )}

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>{displayProjectName(name)}</div>
          <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>לחץ לכניסה לפרויקט</div>
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirm, setConfirm] = useState(null);
  const navigate = useNavigate();
  const workerRole = getWorkerRole();
  const isPM = workerRole === 'project_manager';

  useEffect(() => { fetchProjects(); }, []);

  async function fetchProjects() {
    const res = await api.get('/projects');
    const data = res.data;
    // אם כל הפרויקטים הם של גבסו — מיין לפי הסדר הקבוע
    const isGavso = data.length > 0 && data.every(p => GAVSO_PROJECTS.includes(p.projectName));
    if (isGavso) {
      data.sort((a, b) => GAVSO_PROJECTS.indexOf(a.projectName) - GAVSO_PROJECTS.indexOf(b.projectName));
    }
    setProjects(data);
  }

  // פרויקט יחיד → כניסה ישירה
  useEffect(() => {
    if (projects.length === 1) {
      navigate(`/project-hub/${encodeURIComponent(projects[0].projectName)}`);
    }
  }, [projects]);

  function handleSubmitAdd(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setConfirm({ name: newName.trim() });
  }

  async function handleConfirmAdd() {
    await api.post('/projects', { projectName: confirm.name });
    setConfirm(null);
    setShowAdd(false);
    setNewName('');
    fetchProjects();
  }

  return (
    <div>
      <Navbar title="ניהול קהילה" />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 className="page-title">פרויקטים</h2>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ הוסף פרויקט</button>
        </div>
        <div className="cards-grid">
          {projects.map(p => {
            const isGavsoProject = GAVSO_PROJECTS.includes(p.projectName) && projects.every(x => GAVSO_PROJECTS.includes(x.projectName));
            const logo = isGavsoProject
              ? GAVSO_LOGOS[p.projectName]
              : isPM ? (CLIENT_LOGOS[p.projectName] || PROJECT_LOGOS[p.projectName]) : PROJECT_LOGOS[p.projectName];
            return (
              <div key={p.projectName} className="card" onClick={() => navigate(`/project-hub/${encodeURIComponent(p.projectName)}`)} style={{ textAlign: 'center', cursor: 'pointer', background: isGavsoProject ? '#fff' : undefined, minHeight: isGavsoProject ? 320 : undefined, display: isGavsoProject ? 'flex' : undefined, flexDirection: isGavsoProject ? 'column' : undefined, alignItems: isGavsoProject ? 'center' : undefined, justifyContent: isGavsoProject ? 'center' : undefined }}>
                {logo && (p.projectName === 'אייזיקחריף'
                  ? <div style={{ overflow:'hidden', height:87, display:'flex', alignItems:'flex-start', justifyContent:'center', width:'100%', marginBottom:10 }}>
                      <img src={logo} alt={p.projectName} style={{ width:'70%', objectFit:'cover', objectPosition:'top' }} />
                    </div>
                  : p.projectName === 'ג החדשה'
                  ? <div style={{ overflow:'hidden', height:87, display:'flex', alignItems:'flex-start', justifyContent:'center', width:'100%', marginBottom:10 }}>
                      <img src={logo} alt={p.projectName} style={{ width:'70%', objectFit:'cover', objectPosition:'top' }} />
                    </div>
                  : <img src={logo} alt={p.projectName} style={{ maxHeight: isGavsoProject ? (GAVSO_LOGO_HEIGHT[p.projectName] || 240) : 80, width: isGavsoProject ? '100%' : undefined, objectFit: 'contain', marginBottom: 10 }} />
                )}
                <h3>{displayProjectName(p.projectName)}</h3>
              </div>
            );
          })}
        </div>
      </div>

      {showAdd && !confirm && (
        <Modal title="הוספת פרויקט חדש" onClose={() => setShowAdd(false)}>
          <form onSubmit={handleSubmitAdd}>
            <div className="form-group">
              <label>שם הפרויקט</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} required autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary">המשך</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}

      {confirm && (
        <Modal title="אישור הוספת פרויקט" onClose={() => setConfirm(null)}>
          <p style={{ marginBottom: 20 }}>
            האם אתה בטוח שברצונך להוסיף את הפרויקט <strong>{confirm.name}</strong>?
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleConfirmAdd}>אישור</button>
            <button className="btn btn-secondary" onClick={() => setConfirm(null)}>ביטול</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
